<script setup lang="ts" generic="TData">
import { ref, computed } from 'vue'
import {
  useVueTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  FlexRender,
  type ColumnDef,
  type SortingState,
  type PaginationState,
} from '@tanstack/vue-table'

const props = withDefaults(
  defineProps<{
    columns: ColumnDef<TData, unknown>[]
    data: TData[]
    loading?: boolean
    pageSize?: number
    paginate?: boolean
    onRowClick?: (row: TData) => void
    // Fires before a wrapping ContextMenu opens, so the menu knows which row it's for
    onRowContextmenu?: (row: TData, event: MouseEvent) => void
  }>(),
  { pageSize: 25, paginate: true },
)

const sorting = ref<SortingState>([])
const pagination = ref<PaginationState>({ pageIndex: 0, pageSize: props.pageSize })

const table = useVueTable({
  get data() {
    return props.data
  },
  get columns() {
    return props.columns
  },
  getCoreRowModel: getCoreRowModel(),
  getSortedRowModel: getSortedRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  onSortingChange: (updater) => {
    sorting.value = typeof updater === 'function' ? updater(sorting.value) : updater
  },
  onPaginationChange: (updater) => {
    pagination.value =
      typeof updater === 'function' ? updater(pagination.value) : updater
  },
  state: {
    get sorting() {
      return sorting.value
    },
    get pagination() {
      return pagination.value
    },
  },
})

const pageCount = computed(() => table.getPageCount())
const canPrevPage = computed(() => table.getCanPreviousPage())
const canNextPage = computed(() => table.getCanNextPage())
</script>

<template>
  <div class="flex flex-col bg-surface-container overflow-hidden">
    <div class="overflow-x-auto">
      <table class="w-full text-left border-collapse">
        <thead>
          <tr
            v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
            class="bg-surface-container-lowest/80 border-b border-border"
          >
            <th
              v-for="header in headerGroup.headers"
              :key="header.id"
              :class="[
                'px-4 py-3 font-headline text-[0.7rem] font-black uppercase tracking-widest text-outline select-none',
                header.column.getCanSort() ? 'cursor-pointer hover:text-on-surface-variant transition-colors' : '',
              ]"
              @click="header.column.getToggleSortingHandler()?.($event)"
            >
              <div class="flex items-center gap-1">
                <FlexRender
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />
                <span
                  v-if="header.column.getCanSort()"
                  class="material-symbols-outlined text-[14px] text-outline/50"
                >
                  {{
                    header.column.getIsSorted() === 'asc'
                      ? 'arrow_upward'
                      : header.column.getIsSorted() === 'desc'
                        ? 'arrow_downward'
                        : 'unfold_more'
                  }}
                </span>
              </div>
            </th>
          </tr>
        </thead>

        <tbody class="font-body text-xs">
          <template v-if="loading">
            <tr>
              <td :colspan="columns.length" class="px-4 py-8 text-center text-outline font-mono text-xs uppercase tracking-widest">
                Loading...
              </td>
            </tr>
          </template>
          <template v-else-if="table.getRowModel().rows.length === 0">
            <tr>
              <td :colspan="columns.length" class="px-4 py-8 text-center text-outline font-mono text-xs uppercase tracking-widest">
                No records
              </td>
            </tr>
          </template>
          <template v-else>
            <tr
              v-for="(row, i) in table.getRowModel().rows"
              :key="row.id"
              :class="[
                'border-b border-border hover:bg-surface-container-low transition-colors',
                i % 2 === 0 ? 'bg-surface-container-lowest/30' : '',
                onRowClick ? 'cursor-pointer' : '',
              ]"
              @click="onRowClick?.(row.original)"
              @contextmenu="onRowContextmenu?.(row.original, $event)"
            >
              <td
                v-for="cell in row.getVisibleCells()"
                :key="cell.id"
                :class="['px-4 py-3 text-on-surface-variant', (cell.column.columnDef.meta as any)?.tdClass ?? '']"
              >
                <FlexRender :render="cell.column.columnDef.cell" :props="cell.getContext()" />
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div
      v-if="paginate && pageCount > 1"
      class="flex items-center justify-between px-4 py-3 bg-surface-container-lowest/50 border-t border-border"
    >
      <span class="font-mono text-[0.65rem] text-outline uppercase tracking-widest">
        Page {{ pagination.pageIndex + 1 }} / {{ pageCount }}
        &nbsp;·&nbsp;
        {{ data.length }} records
      </span>
      <div class="flex items-center gap-1">
        <button
          class="p-1.5 text-outline hover:text-on-surface hover:bg-surface-container-high transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          :disabled="!canPrevPage"
          @click="table.previousPage()"
        >
          <span class="material-symbols-outlined text-[18px]">chevron_left</span>
        </button>
        <button
          class="p-1.5 text-outline hover:text-on-surface hover:bg-surface-container-high transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          :disabled="!canNextPage"
          @click="table.nextPage()"
        >
          <span class="material-symbols-outlined text-[18px]">chevron_right</span>
        </button>
      </div>
    </div>
  </div>
</template>
